import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useActiveGroup } from '../hooks/useActiveGroup';
import { useAuth } from '../hooks/useAuth';
import { 
  Users, 
  Shuffle, 
  Trophy, 
  AlertCircle, 
  Star, 
  Calendar, 
  Save,
  Plus,
  Link as LinkIcon,
  Check,
  X,
  Clock,
  MapPin,
  ExternalLink,
  UserCheck
} from 'lucide-react';
import CreateGameModal from '../components/games/CreateGameModal';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '../contexts/ToastContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import PlanLimitModal from '../components/PlanLimitModal';
import { nivelParaNota, gerarCaracteristicasPorNivel } from '../utils/playerUtils';

interface Player {
  id: string;
  nome: string;
  posicao: string;
  nivel: number;
  caracteristicas?: {
    velocidade: number;
    finalizacao: number;
    passe: number;
    drible: number;
    defesa: number;
    fisico: number;
  };
}

interface Team {
  name: string;
  players: Player[];
  averageRating: number;
}

interface GameConfirmation {
  id: string;
  player_name: string;
  player_phone: string | null;
  status: 'confirmed' | 'declined';
  notes: string | null;
}

interface Game {
  id: string;
  titulo: string;
  title: string;
  datetime: string;
  location: string | null;
  public_token: string;
}

const SorteioPage: React.FC = () => {
  const navigate = useNavigate();
  const { limits } = usePlanFeatures();
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('game');
  const { supabase } = useSupabase();
  const { activeGroup } = useActiveGroup();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [benchPlayers, setBenchPlayers] = useState<Player[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [groupType, setGroupType] = useState<'campo' | 'society' | 'quadra' | null>(null);
  const [gameDate, setGameDate] = useState(new Date().toISOString().split('T')[0]);
  const [existingGame, setExistingGame] = useState<boolean>(false);
  const [showCreateGameModal, setShowCreateGameModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [gameConfirmations, setGameConfirmations] = useState<GameConfirmation[]>([]);
  const [showConfirmations, setShowConfirmations] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Função para fazer parsing correto da data/hora
  const parseGameDateTime = (dateTimeString: string) => {
    try {
      // Primeiro tentar como ISO string
      const isoDate = new Date(dateTimeString);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
      
      // Se falhar, tentar parse manual
      if (dateTimeString.includes(' ')) {
        return parse(dateTimeString, 'yyyy-MM-dd HH:mm:ss', new Date());
      }
      
      // Fallback para apenas data
      return parse(dateTimeString, 'yyyy-MM-dd', new Date());
    } catch (error) {
      console.error('Erro ao fazer parse da data:', error);
      return new Date(dateTimeString); // Fallback
    }
  };
 
  useEffect(() => {
    const fetchData = async () => {
      if (!activeGroup) return;

      try {
        console.log('Fetching data for activeGroup:', activeGroup.id, 'gameId:', gameId);
        
        // Get group type
        const { data: groupData } = await supabase
          .from('grupos')
          .select('tipo')
          .eq('id', activeGroup.id)
          .single();

        if (groupData) {
          setGroupType(groupData.tipo as 'campo' | 'society' | 'quadra');
        }

        // If gameId is provided, load that specific game
        if (gameId) {
          console.log('Loading specific game with ID:', gameId);
          const { data: gameData, error: gameError } = await supabase
            .from('jogos')
            .select('*')
            .eq('id', gameId)
            .single();

          if (gameError) {
            console.error('Error loading game data:', gameError);
            setError('Erro ao carregar dados do jogo');
            return;
          }

          if (gameData) {
            console.log('Game data loaded:', gameData);
            
            // Extract date from the game data
            const gameDateTime = parseGameDateTime(gameData.data);
            const gameDateString = gameDateTime.toISOString().split('T')[0];
            
            console.log('Setting game date to:', gameDateString);
            setSelectedGame(gameData);
            setGameDate(gameDateString);
            setExistingGame(true);
            
            // Load teams if they exist
            if (gameData.times) {
              setTeams(gameData.times as Team[]);
            }
            
            // Load confirmations for this game
            const { data: confirmationsData } = await supabase
              .from('game_confirmations')
              .select('*')
              .eq('game_id', gameId)
              .eq('status', 'confirmed')
              .order('confirmed_at');

            if (confirmationsData) {
              console.log('Game confirmations loaded:', confirmationsData.length);
              setGameConfirmations(confirmationsData);
              setShowConfirmations(true);
            }
          } else {
            console.warn('No game data found for gameId:', gameId);
            setError('Jogo não encontrado');
          }
        } else {
          // No specific game selected, check for existing game on current date
          console.log('No gameId provided, checking for existing game on date:', gameDate);
          const { data: existingGameData, error: checkError } = await supabase
            .from('jogos')
            .select('id, times')
            .eq('grupo_id', activeGroup.id)
            .eq('data', gameDate)
            .maybeSingle();

          if (checkError) {
            console.error('Error checking existing game:', checkError);
          }

          setExistingGame(!!existingGameData);
          if (existingGameData?.times) {
            setTeams(existingGameData.times as Team[]);
          } else {
            setTeams([]);
          }
        }

        // Fetch active players with their characteristics
        const { data: playersData } = await supabase
          .from('jogadores')
          .select(`
            *,
            caracteristicas:jogadores_caracteristicas(
              velocidade,
              finalizacao,
              passe,
              drible,
              defesa,
              fisico
            )
          `)
          .eq('grupo_id', activeGroup.id)
          .eq('ativo', true)
          .order('nome');

        if (playersData) {
          setPlayers(playersData.map(player => ({
            ...player,
            caracteristicas: player.caracteristicas?.[0]
          })));
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setError('Erro ao carregar dados dos jogadores');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [supabase, activeGroup, gameId]);

  // Separate useEffect for handling gameDate changes when no specific game is selected
  useEffect(() => {
    if (!gameId && activeGroup) {
      console.log('Game date changed to:', gameDate, 'checking for existing game');
      const checkExistingGame = async () => {
        const { data: existingGameData, error: checkError } = await supabase
          .from('jogos')
          .select('id, titulo, times, public_token, data, local')
          .eq('grupo_id', activeGroup.id)
          .eq('data', gameDate)
          .maybeSingle();

        if (checkError) {
          console.error('Error checking existing game:', checkError);
          return;
        }

        console.log('Existing game data for date:', existingGameData);
        setExistingGame(!!existingGameData);
        
        // Se encontrou um jogo existente, definir como selectedGame
        if (existingGameData) {
          setSelectedGame(existingGameData);
          console.log('Set selectedGame to existing game:', existingGameData);
        } else {
          setSelectedGame(null);
          console.log('No existing game found, cleared selectedGame');
        }
        
        if (existingGameData?.times) {
          setTeams(existingGameData.times as Team[]);
          console.log('Loaded existing teams:', existingGameData.times);
        } else {
          setTeams([]);
          console.log('No existing teams found');
        }
      };
      
      checkExistingGame();
    }
  }, [gameDate, activeGroup, gameId, supabase]);

  const importConfirmedPlayers = () => {
    const confirmedPlayerNames = gameConfirmations.map(c => c.player_name.toLowerCase());
    const matchedPlayers = players.filter(player => 
      confirmedPlayerNames.includes(player.nome.toLowerCase())
    );
    
    setSelectedPlayers(matchedPlayers.map(p => p.id));
    setTeams([]);
    showSuccess(
      'Jogadores importados!', 
      `${matchedPlayers.length} jogadores confirmados foram selecionados`
    );
  };

  const calculatePlayerRating = (player: Player) => {
    if (!player.caracteristicas) {
      return player.nivel * 20; // Base rating if no characteristics
    }

    // Weight characteristics based on position
    const weights = {
      goleiro: {
        velocidade: 0.15,
        finalizacao: 0.05,
        passe: 0.15,
        drible: 0.05,
        defesa: 0.4,
        fisico: 0.2
      },
      zagueiro: {
        velocidade: 0.2,
        finalizacao: 0.05,
        passe: 0.15,
        drible: 0.1,
        defesa: 0.3,
        fisico: 0.2
      },
      lateral: {
        velocidade: 0.25,
        finalizacao: 0.1,
        passe: 0.2,
        drible: 0.15,
        defesa: 0.2,
        fisico: 0.1
      },
      'meio-campo': {
        velocidade: 0.2,
        finalizacao: 0.15,
        passe: 0.25,
        drible: 0.2,
        defesa: 0.1,
        fisico: 0.1
      },
      atacante: {
        velocidade: 0.25,
        finalizacao: 0.3,
        passe: 0.15,
        drible: 0.2,
        defesa: 0.05,
        fisico: 0.05
      }
    };

    const positionWeights = weights[player.posicao as keyof typeof weights] || {
      velocidade: 0.2,
      finalizacao: 0.2,
      passe: 0.15,
      drible: 0.15,
      defesa: 0.15,
      fisico: 0.15
    };

    return Object.entries(player.caracteristicas).reduce((sum, [key, value]) => {
      return sum + (value * (positionWeights[key as keyof typeof positionWeights] || 0));
    }, 0);
  };

  const getPlayersPerTeam = () => {
    switch (groupType) {
      case 'campo':
        return 11;
      case 'society':
        return 7;
      case 'quadra':
        return 5;
      default:
        return 5;
    }
  };

  const getMinimumPlayers = () => {
    switch (groupType) {
      case 'campo':
        return 14; // 7 per team minimum
      case 'society':
        return 10; // 5 per team minimum
      case 'quadra':
        return 6; // 3 per team minimum
      default:
        return 6;
    }
  };
// Função util para embaralhar arrays (Fisher-Yates)
const shuffleArray = <T,>(array: T[]): T[] => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const drawTeams = () => {
  if (!limits.canCreateGame) {
    setShowPlanModal(true);
    return;
  }

  if (selectedPlayers.length < 4) {
    setError("Selecione pelo menos 4 jogadores para o sorteio");
    return;
  }

  const isGK = (p: Player) => p.posicao?.toLowerCase?.() === "goleiro";

  const selectedPlayerObjects = shuffleArray(
    players.filter((p) => selectedPlayers.includes(p.id))
  );

  const playersPerTeam = getPlayersPerTeam();
  const totalPlayers = selectedPlayerObjects.length;
  const mainTeamCount = Math.floor(totalPlayers / playersPerTeam);
  const newTeams: Team[] = [];
  const teamColors = ["green", "yellow", "orange", "purple", "blue", "red"];

  // Criar times principais
  for (let i = 0; i < mainTeamCount; i++) {
    newTeams.push({
      name: `Time ${i + 1}`,
      // @ts-ignore
      color: teamColors[i % teamColors.length],
      players: [],
      averageRating: 0,
    });
  }

  // Separar goleiros e jogadores de linha
  const goalkeepers = selectedPlayerObjects.filter(isGK);
  const fieldPlayers = selectedPlayerObjects.filter((p) => !isGK(p));

  // Função para criar goleiro fictício com rating calculado
  const createFakeGoalkeeper = (suffix: string): Player => {
    const nivelIntermediario = 3; // Nível intermediário
    const notaIntermediaria = nivelParaNota(nivelIntermediario);
    const caracteristicasIntermediarias = gerarCaracteristicasPorNivel(notaIntermediaria);
    
    const fakeGK: Player = {
      id: `fake-goleiro-${suffix}`,
      nome: `Goleiro Fictício ${suffix}`,
      posicao: "goleiro",
      nivel: nivelIntermediario,
      caracteristicas: caracteristicasIntermediarias,
      isFake: true,
      rating: 0,
    };
    fakeGK.rating = calculatePlayerRating(fakeGK);
    return fakeGK;
  };

  // Garantir 1 goleiro por time
  newTeams.forEach((team, index) => {
    let gk: Player | undefined = goalkeepers.shift();
    if (!gk) {
      gk = createFakeGoalkeeper(index.toString());
    } else if (!gk.rating) {
      gk.rating = calculatePlayerRating(gk);
    }
    team.players.push(gk);
  });

  const recalcAverage = (team: Team) => {
    if (team.players.length === 0) {
      team.averageRating = 0;
      return;
    }
    team.averageRating =
      team.players.reduce((sum, p) => sum + calculatePlayerRating(p), 0) /
      team.players.length;
  };

  // Agrupar jogadores por posição
  const playersByPosition: Record<string, Player[]> = {};
  fieldPlayers.forEach((p) => {
    const pos = p.posicao?.toLowerCase() || "undefined";
    if (!playersByPosition[pos]) playersByPosition[pos] = [];
    playersByPosition[pos].push(p);
  });

  // Distribuir jogadores respeitando posições e rating
  const mainTeams = newTeams.slice(0, mainTeamCount);

  Object.keys(playersByPosition).forEach((pos) => {
    const group = shuffleArray(playersByPosition[pos]);
    group.sort((a, b) => calculatePlayerRating(b) - calculatePlayerRating(a));

    for (const player of group) {
      let inserted = false;

      // Tenta colocar jogador no time que tem menos dessa posição
      const sortedTeams = mainTeams
        .slice()
        .sort((a, b) => {
          const countA = a.players.filter((p) => (p.posicao?.toLowerCase() || "") === pos).length;
          const countB = b.players.filter((p) => (p.posicao?.toLowerCase() || "") === pos).length;
          return countA - countB;
        });

      for (const t of sortedTeams) {
        if (t.players.length < playersPerTeam) {
          t.players.push(player);
          recalcAverage(t);
          inserted = true;
          break;
        }
      }

      if (!inserted) {
        // Se não couber, coloca no time com menor rating
        const targetTeam = mainTeams.reduce((a, b) => (a.averageRating < b.averageRating ? a : b));
        if (targetTeam.players.length < playersPerTeam) {
          targetTeam.players.push(player);
          recalcAverage(targetTeam);
        }
      }
    }
  });

  // Balanceamento iterativo de rating e posições
  const maxIterations = 50;
  let iteration = 0;

  const getRatingDiff = () => {
    const ratings = mainTeams.map((t) => t.averageRating);
    return Math.max(...ratings) - Math.min(...ratings);
  };

  while (iteration < maxIterations && getRatingDiff() > 1) {
    iteration++;

    const strongest = mainTeams.reduce((a, b) => (a.averageRating > b.averageRating ? a : b));
    const weakest = mainTeams.reduce((a, b) => (a.averageRating < b.averageRating ? a : b));

    let bestSwap: { from: Player; to: Player } | null = null;
    let bestDiff = getRatingDiff();

    for (const pStrong of strongest.players) {
      if (isGK(pStrong)) continue;
      for (const pWeak of weakest.players) {
        if (isGK(pWeak)) continue;

        const newStrongRating =
          (strongest.players.reduce((s, p) => s + calculatePlayerRating(p), 0)
            - calculatePlayerRating(pStrong)
            + calculatePlayerRating(pWeak)) / strongest.players.length;

        const newWeakRating =
          (weakest.players.reduce((s, p) => s + calculatePlayerRating(p), 0)
            - calculatePlayerRating(pWeak)
            + calculatePlayerRating(pStrong)) / weakest.players.length;

        const ratingsAfterSwap = mainTeams.map((t) => {
          if (t === strongest) return newStrongRating;
          if (t === weakest) return newWeakRating;
          return t.averageRating;
        });

        const diff = Math.max(...ratingsAfterSwap) - Math.min(...ratingsAfterSwap);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestSwap = { from: pStrong, to: pWeak };
        }
      }
    }

    if (!bestSwap) break;

    strongest.players = strongest.players.filter((p) => p.id !== bestSwap.from.id);
    weakest.players = weakest.players.filter((p) => p.id !== bestSwap.to.id);
    strongest.players.push(bestSwap.to);
    weakest.players.push(bestSwap.from);

    mainTeams.forEach(recalcAverage);
  }

  // Criar times incompletos se sobrarem jogadores
  let remainingPlayers = selectedPlayerObjects.filter(
    (p) => !newTeams.flatMap((t) => t.players).includes(p)
  );

  while (remainingPlayers.length > 0) {
    const team: Team = {
      name: `Time ${newTeams.length + 1} (Incompleto)`,
      // @ts-ignore
      color: teamColors[newTeams.length % teamColors.length],
      players: [],
      averageRating: 0,
    };

    // Adicionar 1 goleiro se necessário
    const gkIndex = remainingPlayers.findIndex(isGK);
    if (gkIndex >= 0) {
      const gk = remainingPlayers[gkIndex];
      if (!gk.rating) gk.rating = calculatePlayerRating(gk);
      team.players.push(gk);
      remainingPlayers.splice(gkIndex, 1);
    } else {
      team.players.push(createFakeGoalkeeper(newTeams.length.toString()));
    }

    // Preencher com jogadores restantes até playersPerTeam
    while (team.players.length < playersPerTeam && remainingPlayers.length > 0) {
      const p = remainingPlayers.shift()!;
      if (!p.rating) p.rating = calculatePlayerRating(p);
      team.players.push(p);
    }

    // Recalcular média
    recalcAverage(team);

    newTeams.push(team);
  }

  setTeams(newTeams);
  setError(null);
};

  
  
 
  const saveGame = async () => {
  if (!limits.canCreateGame) {
    setShowPlanModal(true);
    return;
  }

  if (!activeGroup || !teams.length || !user) return;

  setSaving(true);
  setError(null);

  try {
    console.log('=== INÍCIO DO SALVAMENTO (v2 - upsert) ===');
    console.log('selectedGame:', selectedGame);
    console.log('gameDate:', gameDate);
    console.log('teams.length:', teams.length);
    console.log('selectedPlayers.length:', selectedPlayers.length);

    // 1) Verifica se já existe jogo para (grupo_id, data)
    let existing = selectedGame;
    if (!existing) {
      const { data: maybeExisting, error: fetchErr } = await supabase
        .from('jogos')
        .select('id, public_token, criado_por')
        .eq('grupo_id', activeGroup.id)
        .eq('data', gameDate)
        .maybeSingle();

      if (fetchErr) {
        console.warn('Falha ao checar jogo existente:', fetchErr);
      }
      if (maybeExisting) existing = maybeExisting as any;
    }

    // 2) Monta o payload para UPSERT (conflito em grupo_id,data)
    // Se a data mudou em relação ao jogo existente, não incluir o ID para criar novo jogo
    const dateChanged = existing && existing.data && existing.data !== gameDate;
    
    const payload: any = {
      // só inclui o id se existir E a data não mudou (para update do mesmo registro)
      ...(existing?.id && !dateChanged ? { id: existing.id } : {}),
      grupo_id: activeGroup.id,
      data: gameDate,
      titulo: existing?.titulo || `Jogo em ${format(new Date(gameDate), 'dd/MM/yyyy', { locale: ptBR })}`,
      times: teams, // JSON com o sorteio
      criado_por: dateChanged ? user.id : (existing?.criado_por || user.id),
      public_token: existing?.public_token || (crypto as any).randomUUID?.() || `${Date.now()}-${Math.random()}`
    };

    // 3) Upsert (resolve conflito de "já existe jogo nesse dia")
    const { data: upserted, error: upsertErr } = await supabase
      .from('jogos')
      .upsert(payload, { onConflict: 'grupo_id,data' })
      .select()
      .single();

    if (upsertErr) {
      console.error('Erro no upsert de jogos:', upsertErr);
      throw new Error(upsertErr.message || 'Erro ao salvar jogo');
    }

    const game = upserted;
    console.log('Jogo salvo/atualizado com sucesso:', game);

    // 4) Limpa e recria participações
    const { error: deleteParticipationsError } = await supabase
      .from('jogos_jogadores')
      .delete()
      .eq('jogo_id', game.id);

    if (deleteParticipationsError) {
      console.error('Erro ao limpar participações:', deleteParticipationsError);
      throw new Error(deleteParticipationsError.message || 'Erro ao atualizar participações');
    }

    if (selectedPlayers.length > 0) {
      const participations = selectedPlayers.map((playerId) => ({
        jogo_id: game.id,
        jogador_id: playerId,
      }));

      const { error: participationError } = await supabase
        .from('jogos_jogadores')
        .insert(participations);

      if (participationError) {
        console.error('Erro ao inserir participações:', participationError);
        throw new Error(participationError.message || 'Erro ao registrar participações');
      }
    }

    // 5) Atualiza estados locais
    setSelectedGame(game);
    setExistingGame(true);

    console.log('=== SALVAMENTO CONCLUÍDO COM SUCESSO (v2) ===');

    // Como setSelectedGame é async, usa "game" direto pra definir a mensagem
    showSuccess(
      existing ? 'Times atualizados!' : 'Jogo salvo com sucesso!',
      existing ? 'Os times foram atualizados para este jogo' : 'O jogo foi criado e os times foram salvos'
    );
  } catch (err: any) {
    console.error('=== ERRO NO SALVAMENTO (v2) ===');
    const msg = err?.message || 'Erro desconhecido ao salvar jogo';
    console.error('Mensagem do erro:', msg);
    setError(msg);
    showError('Erro ao salvar', msg);
  } finally {
    setSaving(false);
  }
};

  if (!activeGroup) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhum grupo selecionado</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione um grupo ativo para realizar o sorteio
        </p>
      </div>
    );
  }
// Defina as cores disponíveis
const colors = [
  '#F87171', // vermelho
  '#60A5FA', // azul
  '#34D399', // verde
  '#FBBF24', // amarelo
  '#A78BFA', // roxo
  '#F472B6', // rosa
];

// Mapear os times e adicionar uma cor de outline aleatória
const teamsWithColors = teams.map((team, i) => ({
  ...team,
  outlineColor: colors[i % colors.length], // cada time recebe uma cor diferente
}));

  return (
  <div>
    <div className="flex flex-col mb-6 gap-4">
      <div>
        <h1 className="text-2xl font-bold mb-2">Sorteio de Times</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione a partir de 4 jogadores e sorteie os times automaticamente
        </p>

        {/* Mobile */}
<div className="flex flex-col sm:hidden gap-2">
  <div>
    <label htmlFor="gameDate" className="block text-sm font-medium mb-1">
      Data do Jogo
    </label>
    <input
      type="date"
      id="gameDate"
      className="input w-full"
      value={gameDate}
      onChange={(e) => setGameDate(e.target.value)}
    />
  </div>

  {/* Botões lado a lado */}
  <div className="flex gap-2">
    <button
      onClick={() => navigate('/jogos')}
      className="btn-outline flex-1 flex justify-center items-center"
      title="Gerenciar jogos criados"
    >
      <Calendar className="w-4 h-4 mr-2" />
      Meus Jogos
    </button>

    <button
      onClick={() => setShowCreateGameModal(true)}
      className="btn-outline flex-1 flex justify-center items-center"
      title="Criar jogo com confirmação de presença"
    >
      <LinkIcon className="w-4 h-4 mr-2" />
      Criar Jogo
    </button>
  </div>

  {/* Botão sortear times */}
  <button
    onClick={async () => {
      drawTeams();
      await saveGame();
    }}
    className="btn-primary w-full flex justify-center items-center"
    disabled={selectedPlayers.length < 4 || !limits.canCreateGame || saving}
  >
    <Shuffle className="w-4 h-4 mr-2" />
    {saving ? 'Salvando...' : 'Sortear Times'}
  </button>
</div>


        {/* Desktop */}
        <div className="hidden sm:flex sm:items-center sm:justify-between gap-4">
          <div className="flex items-end gap-4">
            {/* Input de data */}
            <div className="flex flex-col">
              <label htmlFor="gameDate" className="block text-sm font-medium mb-1">
                Data do Jogo
              </label>
              <input
                type="date"
                id="gameDate"
                className="input"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
              />
            </div>

            {/* Botões de ações */}
            <button
              onClick={() => navigate('/jogos')}
              className="btn-outline flex items-center justify-center h-[40px]"
              title="Gerenciar jogos criados"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Meus Jogos
            </button>

            <button
              onClick={() => setShowCreateGameModal(true)}
              className="btn-outline flex items-center justify-center h-[40px]"
              title="Criar jogo com confirmação de presença"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Criar Jogo
            </button>
          </div>

          {/* Botão único para sortear e salvar */}
          <button
            onClick={async () => {
              drawTeams();
              await saveGame();
            }}
            className="btn-primary flex items-center justify-center h-[40px]"
            disabled={selectedPlayers.length < 4 || !limits.canCreateGame || saving}
          >
            <Shuffle className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Sortear Times'}
          </button>
        </div>
      </div>
    </div>



      {existingGame && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>Já existe um jogo registrado para esta data. Salvar novamente irá substituir o sorteio anterior.</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Game Info and Confirmations */}
      {selectedGame && (
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-lg font-bold truncate">{selectedGame.titulo}</h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {selectedGame.data ? format(parseGameDateTime(selectedGame.data), 'PPP', { locale: ptBR }) : 'Data inválida'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>
                    {selectedGame.data ? format(parseGameDateTime(selectedGame.data), 'HH:mm') : 'Horário inválido'}
                  </span>
                </div>
                {selectedGame.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedGame.location}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => window.open(`/confirmar/${selectedGame.public_token}`, '_blank')}
              className="btn-outline"
              title="Abrir página de confirmação"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver Confirmações
            </button>
          </div>

          {gameConfirmations.length > 0 && (
            <div className="border-t dark:border-dark-border pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">
                  Jogadores Confirmados ({gameConfirmations.length})
                </h3>
                <button
                  onClick={importConfirmedPlayers}
                  className="btn-primary text-sm"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Importar Confirmados
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {gameConfirmations.map((confirmation) => (
                  <div
                    key={confirmation.id}
                    className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20"
                  >
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium truncate">
                      {confirmation.player_name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
       
 
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
  {teams.length > 0 ? (
    <>
      {/* Times primeiro */} 
      <div className="space-y-6">
  {teamsWithColors.map((team, index) => (
  <div
    key={index}
    className="card p-6 border-2 rounded-lg"
    style={{ borderColor: team.outlineColor }}
  >
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold">{team.name}</h3>
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-yellow-500" />
        <span className="font-bold">{Math.round(team.averageRating)}</span>
      </div>
    </div>

    <div className="space-y-2">
      {team.players.map((player) => (
        <div
          key={player.id}
          className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-dark-light"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center text-sm font-medium">
              {player.nome.charAt(0).toUpperCase()}
            </div>
            <span className="font-medium">{player.nome}</span>
          </div>
          <div className="flex items-center gap-2">
            {player.posicao && (
              <span className="text-sm px-2 py-0.5 rounded-full bg-gray-100 dark:bg-dark-paper capitalize">
                {player.posicao}
              </span>
            )}
            <div className="flex">
              {Array.from({ length: player.nivel }).map((_, i) => (
                <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
))}
</div>
         
     {/* Jogadores depois */}
<div className="card p-6">
  <h2 className="text-lg font-bold mb-4">Jogadores Disponíveis</h2>

  {/* Checkbox selecionar todos */}
  <div className="flex items-center mb-4">
    <input
      type="checkbox"
      id="selectAll_after"
      className="mr-2 w-4 h-4"
      checked={
        players.length > 0 &&
        players.every(p => selectedPlayers.includes(p.id))
      }
      onChange={(e) => {
        const checked = e.currentTarget.checked;
        if (checked) {
          setSelectedPlayers(players.map(p => p.id)); // seleciona todos
        } else {
          setSelectedPlayers([]); // limpa todos
        }
        setTeams([]);
      }}
    />
    <label htmlFor="selectAll_after" className="cursor-pointer text-sm">
      Selecionar todos
    </label>
  </div>

  <div className="space-y-2">
    {players.map((player) => (
      <label
        key={player.id}
        className="flex items-center p-3 rounded-lg border cursor-pointer transition-colors border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-light"
      >
        <input
          type="checkbox"
          className="mr-3"
          checked={selectedPlayers.includes(player.id)}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            if (checked) {
              setSelectedPlayers(prev => [...prev, player.id]);
            } else {
              setSelectedPlayers(prev => prev.filter(id => id !== player.id));
            }
            setTeams([]);
          }}
        /> 
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="font-medium">{player.nome}</span>
            <div className="flex items-center gap-2">
              {player.posicao && (
                <span className="text-sm px-2 py-0.5 rounded-full bg-gray-100 dark:bg-dark-light capitalize">
                  {player.posicao}
                </span>
              )}
              <div className="flex">
                {Array.from({ length: player.nivel || 0 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </label>
    ))}
  </div>
</div>

    </>
  ) : (
    <>  
      {/* Jogadores primeiro (quando não há times) */}
<div className="card p-6">
  <h2 className="text-lg font-bold mb-4">Jogadores Disponíveis</h2>

  {/* Checkbox selecionar todos */}
  <div className="flex items-center mb-4">
    <input
      type="checkbox"
      id="selectAll_before"
      className="mr-2 w-4 h-4"
      checked={
        players.length > 0 &&
        players.every(p => selectedPlayers.includes(p.id))
      }
      onChange={(e) => {
        const checked = e.currentTarget.checked;
        if (checked) {
          setSelectedPlayers(players.map(p => p.id)); // seleciona todos
        } else {
          setSelectedPlayers([]); // limpa todos
        }
        setTeams([]);
      }}
    />
    <label htmlFor="selectAll_before" className="cursor-pointer text-sm">
      Selecionar todos
    </label>
  </div>

  <div className="space-y-2">
    {players.map((player) => (
      <label
        key={player.id}
        className="flex items-center p-3 rounded-lg border cursor-pointer transition-colors border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-light"
      >
        <input
          type="checkbox"
          className="mr-3"
          checked={selectedPlayers.includes(player.id)}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            if (checked) {
              setSelectedPlayers(prev => [...prev, player.id]);
            } else {
              setSelectedPlayers(prev => prev.filter(id => id !== player.id));
            }
            setTeams([]);
          }}
        />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="font-medium">{player.nome}</span>
            <div className="flex items-center gap-2">
              {player.posicao && (
                <span className="text-sm px-2 py-0.5 rounded-full bg-gray-100 dark:bg-dark-light capitalize">
                  {player.posicao}
                </span>
              )}
              <div className="flex">
                {Array.from({ length: player.nivel || 0 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </label>
    ))}
  </div>
</div>

{/* Espaço vazio para manter layout em desktop */}
<div className="hidden lg:block"></div>

    </>
  )}
</div>

      {/* Modal de Criação de Jogo */}
      <CreateGameModal
        isOpen={showCreateGameModal}
        onClose={() => setShowCreateGameModal(false)}
        onGameCreated={() => {
          setShowCreateGameModal(false);
        }}
      />

      {/* Plan Limit Modal */}
      <PlanLimitModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        title="Recurso não disponível"
        message="A criação e sorteio de jogos não está disponível no seu plano atual. Faça upgrade para acessar este recurso."
        feature="Sorteio de Times"
      />
    </div>
  );
};
 
export default SorteioPage;      